import { ApiError } from '../client';
import { BoxMarksSchema, type BoxMarks } from '../schemas/boxMarks';
import { deliveryDetailMockBoxIds } from './deliveryDetail';

// The mark at which the incident exists and the box is out of service. A fixture level, not a
// refusal threshold: the delivered `DELETE` clears at ANY level (CBX-11149 shipped strict parity
// with `admin` and declined the requested `409`), and it is the banner that withholds the action.
const BOX02_MARK = 6;

// One level per fixture «buzón», so walking the showcase deliveries covers the banner's three
// outcomes — clean, marked, inutilizado — without touching a toggle. Fictional data only.
const SEED: readonly (readonly [string, number])[] = [
  [deliveryDetailMockBoxIds.collected, 3],
  [deliveryDetailMockBoxIds.expired, 5],
  [deliveryDetailMockBoxIds.retrieved, BOX02_MARK],
];

const LEVELS = new Map<string, number>(SEED);

// Dev/test hooks: force a level for any box, or a failure with a chosen code on either verb.
let forcedError: string | null = null;
let forcedClearError: string | null = null;

export function setBoxMarksMockLevel(boxId: string, mark: number): void {
  LEVELS.set(boxId, mark);
}

export function setBoxMarksMockError(code: string | null): void {
  forcedError = code;
}

// The write's counterpart to setBoxMarksMockError, and now the ONLY way to exercise a failing
// clear: the delivered `DELETE` has no refusal of its own, so without this injection the error
// copy could never be reached in the app.
export function setClearBoxMarksMockError(code: string | null): void {
  forcedClearError = code;
}

export function resetBoxMarksMock(): void {
  forcedError = null;
  forcedClearError = null;
  LEVELS.clear();
  SEED.forEach(([boxId, mark]) => LEVELS.set(boxId, mark));
}

export async function fetchBoxMarksMock(boxId: string): Promise<BoxMarks> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedError !== null) {
    throw new ApiError(404, forcedError, { code: forcedError }, `box marks read failed`);
  }
  // An unlisted box is clean, the same «no row» the real read answers with a 204.
  return BoxMarksSchema.parse({ mark: LEVELS.get(boxId) ?? 0 });
}

// Unconditional and idempotent, mirroring the delivered `DELETE`: it clears at any level, BOX02
// included, and never refuses. The panel asked for a `409 box_marks_not_clearable` guard rail and
// backend declined it in favour of strict parity with `admin` (CBX-11149), so the sole defence
// against clearing away a técnico's reference is the banner, which renders no action past the cap.
export async function clearBoxMarksMock(boxId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedClearError !== null) {
    throw new ApiError(
      500,
      forcedClearError,
      { code: forcedClearError },
      `clear box marks failed: ${forcedClearError}`,
    );
  }
  LEVELS.set(boxId, 0);
}
